<template>
  <div class="signup-shell">
    <!-- Ambient Background -->
    <div class="ambient-layer">
      <div class="orb orb-blue" />
      <div class="orb orb-teal" />
      <div class="orb orb-gold" />
    </div>

    <!-- Toolbar Modernisée -->
    <v-toolbar dense tabs color="transparent" dark class="glass-toolbar" elevation="0">
      <v-toolbar-title class="pa-0 pr-0 mt-1" style="width: 100%;">
        <v-layout row align-center>
          <v-flex xs10 sm11 text-xs-left class="toolbar-brand">
            <a href="javascript: history.go(-1)" class="pa-0 back-link">
              <v-btn flat fab small class="pa-0 btn-glass">
                <i class="fas fa-arrow-left headline"></i>
              </v-btn>
            </a>
            <span class="toolbar-title-text">Get Started</span>
          </v-flex>
          <v-flex xs2 sm1 text-xs-right>
            <router-link :to="$store.state.close_url3" class="pa-0 text-xs-right">
              <v-btn flat fab small class="pa-0 btn-glass">
                <i class="fas fa-close headline"></i>
              </v-btn>
            </router-link>
          </v-flex>
        </v-layout>
      </v-toolbar-title>
    </v-toolbar>

    <!-- ═══════════════════════════════════════
         MAIN CONTENT
         ═══════════════════════════════════════ -->
    <v-container class="main-container">
      <v-layout row>
        <v-flex xs12 md8 lg6 offset-md2 offset-lg3>
          <v-slide-y-transition mode="out-in">
            <div class="signup-content">

              <!-- Welcome Block -->
              <div class="welcome-block animate-enter">
                <div class="welcome-eyebrow">New here?</div>
                <h1 class="welcome-title">
                  <span class="petty-blue">Petty</span><span class="cash-green">Cash</span>
                  <span class="sas-gold"> SAS</span>
                </h1>
                <p class="welcome-text">
                  Create your account in seconds and start your journey toward financial freedom.
                </p>
              </div>

              <!-- Progress Steps -->
              <div class="stepper-wrap animate-enter" style="animation-delay: .1s">
                <div class="step-line">
                  <div class="step-progress" :style="{ width: formProgress + '%' }" />
                </div>
                <div class="steps-row">
                  <div
                    v-for="(step, idx) in steps"
                    :key="idx"
                    class="step-dot"
                    :class="{ 'is-active': stepActive(idx), 'is-completed': stepCompleted(idx) }"
                  >
                    <v-icon v-if="stepCompleted(idx)" small color="white">check</v-icon>
                    <span v-else>{{ idx + 1 }}</span>
                    <div class="step-label">{{ step }}</div>
                  </div>
                </div>
              </div>

              <!-- Form Card -->
              <v-card class="glass-form-card pa-4 animate-enter" style="animation-delay: .2s" elevation="0">

                <!-- Full Names -->
                <div class="field-group">
                  <div class="field-icon-wrap">
                    <div class="field-icon-circle teal-glow">
                      <i class="fas fa-user"></i>
                    </div>
                  </div>
                  <div class="field-input-wrap">
                    <v-text-field
                      class="modern-input"
                      id="fullnames"
                      v-model="fullnames"
                      ref="fullnames"
                      :rules="[v => !!v || 'Your Full Names is required']"
                      label="Full Names"
                      single-line
                      color="teal"
                      solo
                      flat
                      @keyup.enter="focusNext('username')"
                    ></v-text-field>
                  </div>
                  <v-btn
                    @click.stop="triggerHelp('fullnames')"
                    flat fab small
                    class="help-btn"
                  >
                    <i class="fas fa-question"></i>
                  </v-btn>
                </div>

                <!-- Username -->
                <div class="field-group">
                  <div class="field-icon-wrap">
                    <div class="field-icon-circle teal-glow">
                      <i class="fas fa-user-circle"></i>
                    </div>
                  </div>
                  <div class="field-input-wrap">
                    <v-text-field
                      class="modern-input"
                      id="username"
                      v-model="username"
                      ref="username"
                      :rules="[v => !!v || 'Username is required']"
                      label="Username"
                      single-line
                      color="teal"
                      solo
                      flat
                      @keyup.enter="focusNext('password')"
                    ></v-text-field>
                  </div>
                  <v-btn
                    @click.stop="triggerHelp('username')"
                    flat fab small
                    class="help-btn"
                  >
                    <i class="fas fa-question"></i>
                  </v-btn>
                </div>

                <!-- Password -->
                <div class="field-group">
                  <div class="field-icon-wrap">
                    <div class="field-icon-circle teal-glow">
                      <i class="fas fa-lock"></i>
                    </div>
                  </div>
                  <div class="field-input-wrap">
                    <v-text-field
                      class="modern-input"
                      id="password"
                      v-model="password"
                      ref="password"
                      label="Enter Password"
                      :rules="[v => !!v || 'Your Password is required (at least 8 characters)']"
                      single-line
                      color="teal"
                      min="8"
                      :append-icon="e1 ? 'visibility' : 'visibility_off'"
                      @click:append="() => (e1 = !e1)"
                      :type="e1 ? 'password' : 'text'"
                      counter
                      solo
                      flat
                      @keyup.enter="focusNext('referral')"
                    ></v-text-field>
                  </div>
                  <v-btn
                    @click.stop="triggerHelp('password')"
                    flat fab small
                    class="help-btn"
                  >
                    <i class="fas fa-question"></i>
                  </v-btn>
                </div>

                <!-- Referral Code -->
                <div class="field-group">
                  <div class="field-icon-wrap">
                    <div class="field-icon-circle blue-glow">
                      <i class="fas fa-code"></i>
                    </div>
                  </div>
                  <div class="field-input-wrap">
                    <ReferralField
                      v-model="referralCode"
                      :state="referralState"
                      :ownerName="referralOwnerName"
                      :locked="referralLocked"
                      @input="onReferralInput"
                    />
                  </div>
                  <v-btn
                    @click.stop="triggerHelp('referral')"
                    flat fab small
                    class="help-btn"
                  >
                    <i class="fas fa-question"></i>
                  </v-btn>
                </div>

                <!-- Phone Number -->
                <div class="field-group">
                  <div class="field-icon-wrap">
                    <div class="field-icon-circle orange-glow">
                      <i class="fas fa-phone"></i>
                    </div>
                  </div>
                  <div class="field-input-wrap phone-wrap">
                    <input
                      @click="clearPhoneField()"
                      id="phone"
                      required
                      type="tel"
                      value="Enter Phone Number"
                      v-model="phone_number"
                      ref="phone_number"
                      name="phone"
                      class="phone-input"
                    />
                  </div>
                  <v-btn
                    @click.stop="triggerHelp('phone')"
                    flat fab small
                    class="help-btn"
                  >
                    <i class="fas fa-question"></i>
                  </v-btn>
                </div>

                <!-- Submit -->
                <div class="submit-wrap text-xs-right">
                  <v-btn
                    class="btn-glass-primary btn-large"
                    round
                    block
                    @click="createNewAccount()"
                    :loading="$store.state.isLoading"
                  >
                    <span>Create Account</span>
                    <v-icon right small>mdi-arrow-right</v-icon>
                  </v-btn>
                </div>

              </v-card>

              <!-- Login Link -->
              <div class="login-link-wrap animate-enter" style="animation-delay: .3s">
                <p class="login-text">
                  Already have an account?
                  <router-link :to="{name: 'login'}" class="login-link">
                    Sign in
                  </router-link>
                </p>
              </div>

              <!-- Trust Footer -->
              <div class="signup-trust-footer animate-enter" style="animation-delay: .4s">
                <div class="trust-pill">
                  <v-icon small color="#00479B">mdi-shield-check</v-icon>
                  <span>Secure Signup</span>
                </div>
                <div class="trust-pill">
                  <v-icon small color="#10B981">mdi-lock</v-icon>
                  <span>Data Encrypted</span>
                </div>
              </div>

            </div>
          </v-slide-y-transition>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import axios from 'axios'
import referralMixin from '@/mixins/referralMixin'
import ReferralField from '@/components/ReferralField.vue'

export default {
  mixins: [referralMixin],
  components: { ReferralField },

  data() {
    return {
      e1: true,
      e2: true,

      fullnames_help: false,
      referal_code_help: false,
      username_help: false,
      password_help: false,
      password_again_help: false,
      phone_number_help: false,

      username: "",
      referalFromURL: false,
      fullnames: "",
      password: "",
      password_again: "",
      phone_number: "Enter Phone Number",
      alert_message: "",
      phoneInput: [],
      form: {
        affiliate_referral: ''
      },

      steps: ['Identity', 'Security', 'Contact'],
    }
  },

  computed: {
    formProgress() {
      let filled = 0
      if (this.fullnames.length >= 4) filled++
      if (this.username.length >= 4) filled++
      if (this.password.length >= 8) filled++
      if (this.phone_number && this.phone_number !== 'Enter Phone Number') filled++
      return (filled / 4) * 100
    }
  },

  mounted() {
    document.title = "PettyCash | SignUp"
    if (this.$store.state.isAuthenticated) {
      this.$store.commit('setSnackBarMessage', "You are currently signed into an account.")
      this.$store.commit('setSnackBarColor', "red darken-4")
      this.$store.commit('activateSnackBar', true)
      this.$router.push("/All")
    }
    this.initialiseIntel()
    this.$store.commit('setSaveItem', "")
  },

  methods: {
    stepActive(idx) {
      const progress = this.formProgress
      if (idx === 0) return this.fullnames.length > 0
      if (idx === 1) return this.username.length > 0 && this.password.length > 0
      if (idx === 2) return this.phone_number && this.phone_number !== 'Enter Phone Number'
      return false
    },

    stepCompleted(idx) {
      const progress = this.formProgress
      if (idx === 0) return this.fullnames.length >= 4 && this.username.length >= 4
      if (idx === 1) return this.password.length >= 8
      if (idx === 2) return this.phone_number && this.phone_number !== 'Enter Phone Number' && this.phoneInput && this.phoneInput.isValidNumber && this.phoneInput.isValidNumber()
      return false
    },

    focusNext(refName) {
      this.$nextTick(() => {
        const el = this.$refs[refName]
        if (el && el.focus) el.focus()
      })
    },

    triggerHelp(field) {
      this.fullnames_help = false
      this.username_help = false
      this.password_help = false
      this.referal_code_help = false
      this.phone_number_help = false

      if (field === 'fullnames') this.fullnames_help = true
      if (field === 'username') this.username_help = true
      if (field === 'password') this.password_help = true
      if (field === 'referral') this.referal_code_help = true
      if (field === 'phone') this.phone_number_help = true

      this.getSignUpFieldHelp()
    },

    initialiseIntel() {
      function getIp(callback) {
        fetch('https://ipinfo.io/json?token=fa5e74d3e65812', { headers: { 'Accept': 'application/json' }})
          .then((resp) => resp.json())
          .catch(() => {
            return { country: 'cm' }
          })
          .then((resp) => callback(resp.country))
      }

      const phoneInputField = document.querySelector("#phone")
      this.phoneInput = window.intlTelInput(phoneInputField, {
        initialCountry: "cm",
        geoIpLookup: getIp,
        preferredCountries: ["cm", "us", "uk", "ci", "cg", "cf", "td", "ng", "gq", "ga"],
        utilsScript: "https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js",
      })
    },

    clearPhoneField() {
      let phone_number = this.phoneInput.getNumber()
      console.log(phone_number)
      if (phone_number === "Enter Phone Number") {
        this.phone_number = ""
      }
    },

    getSignUpFieldHelp() {
      if (this.fullnames_help) {
        this.fullnames_help = false
        this.$store.commit('setDialogTitle', "Full Names")
        this.$store.commit('setDialogMessage', "Here you are required to input all your names.")
        this.$store.commit('activateDialog', true)
      } else if (this.username_help) {
        this.username_help = false
        this.$store.commit('setDialogTitle', "Your User Name")
        this.$store.commit('setDialogMessage', "Here you are required to input your User Name.")
        this.$store.commit('activateDialog', true)
      } else if (this.password_help) {
        this.password_help = false
        this.$store.commit('setDialogTitle', "Your Password")
        this.$store.commit('setDialogMessage', "You are required to input your account password. This password needs to be at least 8 characters long. It is also advisable for you to write down this password so in case u forget, you can know where to get it.")
        this.$store.commit('activateDialog', true)
      } else if (this.password_again_help) {
        this.password_again_help = false
        this.$store.commit('setDialogTitle', "Re-enter Password")
        this.$store.commit('setDialogMessage', "Here you are required to re-enter your password. This is to confirm that you didn't make any typing errors and know your password.")
        this.$store.commit('activateDialog', true)
      } else if (this.phone_number_help) {
        this.phone_number_help = false
        this.$store.commit('setDialogTitle', "Your Phone Number")
        this.$store.commit('setDialogMessage', "Please input your phone number. This number will be used in sending of money and validating transactions. so make sure it is the right number.")
        this.$store.commit('activateDialog', true)
      } else if (this.referal_code_help) {
        this.referal_code_help = false
        this.$store.commit('setDialogTitle', "Who Refered You")
        this.$store.commit('setDialogMessage', "Did you get a referal code to input as you create your account. Here's where to put it.")
        this.$store.commit('activateDialog', true)
      }
    },

    async createNewAccount() {
      if (this.fullnames.length < 4) {
        this.$store.commit('setSnackBarMessage', "Your name are too short, please input atleast 2 of your names")
        this.$store.commit('setSnackBarColor', "red darken-4")
        this.$store.commit('activateSnackBar', true)
        this.$refs.fullnames.focus()
      } else if (this.username.length < 4) {
        this.$store.commit('setSnackBarMessage', "User name is too short, user name needs to be at least 4 characters")
        this.$store.commit('setSnackBarColor', "red darken-4")
        this.$store.commit('activateSnackBar', true)
        this.$refs.username.focus()
      } else if (this.password.length < 8) {
        this.$store.commit('setSnackBarMessage', "Password is too short, needs to be at least 8 characters")
        this.$store.commit('setSnackBarColor', "red darken-4")
        this.$store.commit('activateSnackBar', true)
        this.$refs.password.focus()
      } else if (!this.phoneInput.isValidNumber()) {
        this.$store.commit('setSnackBarMessage', "Phone number is invalid. Please input a valid number")
        this.$store.commit('setSnackBarColor', "red darken-4")
        this.$store.commit('activateSnackBar', true)
        this.$refs.phone_number.focus()
      } else {
        this.$store.commit('setIsLoading', true)

        var referal_flag = true
        var contact_flag = false

        const referralCodeToCheck = this.getReferralPayload()

        if (referralCodeToCheck && referralCodeToCheck.length > 0) {
          await axios
            .get('/api/v1/savings/check/partner/referal_code/' + referralCodeToCheck + '/')
            .then(response => {
              referal_flag = response.data
            })
            .catch(error => {
              if (error.response) {
                this.$store.commit('setSnackBarMessage', error.response.status + " " + error.response.statusText)
                this.$store.commit('setSnackBarColor', "red darken-4")
                this.$store.commit('activateSnackBar', true)
              } else {
                this.$store.commit('setSnackBarMessage', "An error occured, please check your internet connection and try again later.")
                this.$store.commit('setSnackBarColor', "red darken-4")
                this.$store.commit('activateSnackBar', true)
              }
            })
        }

        if (this.phoneInput.isValidNumber()) {
          await axios
            .get('/api/v1/savings/check/client/contact/' + this.phoneInput.getNumber() + '/')
            .then(response => {
              contact_flag = response.data
            })
            .catch(error => {
              if (error.response) {
                this.$store.commit('setSnackBarMessage', error.response.status + " " + error.response.statusText)
                this.$store.commit('setSnackBarColor', "red darken-4")
                this.$store.commit('activateSnackBar', true)
              } else {
                this.$store.commit('setSnackBarMessage', "An error occured, please check your internet connection and try again later.")
                this.$store.commit('setSnackBarColor', "red darken-4")
                this.$store.commit('activateSnackBar', true)
              }
            })
        }

        if (referal_flag && !contact_flag) {
          const formData = {
            username: this.username,
            password: this.password
          }

          await axios
            .post('/api/v1/users/', formData)
            .then(response => {
              console.log(response.data)
              const payload = {
                'username': this.username,
                'password': this.password,
                'phone_number': this.phoneInput.getNumber(),
                'fullnames': this.fullnames,
                'referal_code': this.getReferralPayload()
              }
              console.log('[SignUp.vue] Payload à envoyer:', { payload, referralCode: this.referralCode, getReferralPayload: this.getReferralPayload() })
              axios
                .post('/api/v1/home/create/account/', payload)
                .then(response => {
                  console.log(response.data[0])
                  this.$store.commit('setSnackBarMessage', response.data[0].text)
                  this.$store.commit('setSnackBarColor', response.data[0].color)
                  this.$store.commit('activateSnackBar', true)

                  var navigation = this.$router
                  if (response.data[0].valid) {
                    axios
                      .post('/api/v1/token/login/', formData)
                      .then(response => {
                        this.$store.state.username = this.username
                        const token = response.data.auth_token
                        this.$store.commit('setToken', token)
                        axios.defaults.headers.common['Authorization'] = "Token " + token
                        localStorage.setItem('token', token)
                        navigation.push('/savings/campaigns/create/new_campaign')
                      })
                      .catch(error => {
                        if (error.response) {
                          for (const property in error.response.data) {
                            this.$store.commit('setSnackBarMessage', error.response.data[property][0])
                            this.$store.commit('setSnackBarColor', "red darken-4")
                            this.$store.commit('activateSnackBar', true)
                          }
                        } else {
                          this.$store.commit('setSnackBarMessage', "An error occured while logging into your account")
                          this.$store.commit('setSnackBarColor', "red darken-4")
                          this.$store.commit('activateSnackBar', true)
                        }
                      })

                    setTimeout(function() {
                      sessionStorage.setItem('url', window.location.href)
                      navigation.push('/savings/campaigns/create/new_campaign')
                    }, 4000)
                  }
                })
                .catch(error => {
                  if (error.response) {
                    this.$store.commit('setSnackBarMessage', error.response.status + " " + error.response.statusText)
                    this.$store.commit('setSnackBarColor', "red darken-4")
                    this.$store.commit('activateSnackBar', true)
                  } else {
                    this.$store.commit('setSnackBarMessage', "An error occured, please check your internet connection and try again later.")
                    this.$store.commit('setSnackBarColor', "red darken-4")
                    this.$store.commit('activateSnackBar', true)
                  }
                })
            })
            .catch(error => {
              if (error.response) {
                for (const property in error.response.data) {
                  this.$store.commit('setSnackBarMessage', error.response.data[property][0])
                  this.$store.commit('setSnackBarColor', "red darken-4")
                  this.$store.commit('activateSnackBar', true)
                }
              } else {
                this.$store.commit('setSnackBarMessage', "An error occured while logging into your account")
                this.$store.commit('setSnackBarColor', "red darken-4")
                this.$store.commit('activateSnackBar', true)
              }
            })
        } else {
          if (!referal_flag) {
            this.$store.commit('setSnackBarMessage', "Referal code is invalid, please either you correct it or allow it blank")
            this.$store.commit('setSnackBarColor', "red darken-4")
            this.$store.commit('activateSnackBar', true)
            this.$refs.referal_code.focus()
          } else if (contact_flag) {
            this.$store.commit('setSnackBarMessage', "This phone number has already been used to create an account, please use another number")
            this.$store.commit('setSnackBarColor', "red darken-4")
            this.$store.commit('activateSnackBar', true)
            this.$refs.phone_number.focus()
          }
        }
        this.$store.commit('setIsLoading', false)
      }
    }
  }
}
</script>

<style scoped>
/* ═══════════════════════════════════════
   DESIGN TOKENS & RESET
   ═══════════════════════════════════════ */
.signup-shell {
  --pc-primary: #00479B;
  --pc-primary-light: #0066CC;
  --pc-teal: #009688;
  --pc-green: #10B981;
  --pc-orange: #F97316;
  --pc-gold: #D4AF37;
  min-height: 100vh;
  background: linear-gradient(180deg, #F0F7FF 0%, #F8FAFC 40%, #FFFFFF 100%);
  position: relative;
  overflow-x: hidden;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
}

.petty-blue { color: #00479B; }
.cash-green { color: #10B981; }
.sas-gold { color: #D4AF37; font-size: 0.55em; font-weight: 700; vertical-align: super; margin-left: 0.15em; }

/* ═══════════════════════════════════════
   AMBIENT BACKGROUND
   ═══════════════════════════════════════ */
.ambient-layer {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}

.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.3;
  animation: orbDrift 20s ease-in-out infinite alternate;
}

.orb-blue {
  width: 500px;
  height: 500px;
  background: #00479B;
  top: -10%;
  right: -5%;
  animation-duration: 25s;
}

.orb-teal {
  width: 400px;
  height: 400px;
  background: #009688;
  bottom: 10%;
  left: -10%;
  animation-duration: 30s;
  animation-delay: -5s;
}

.orb-gold {
  width: 300px;
  height: 300px;
  background: #D4AF37;
  top: 40%;
  left: 40%;
  opacity: 0.15;
  animation-duration: 22s;
  animation-delay: -10s;
}

@keyframes orbDrift {
  from { transform: translate(0, 0) scale(1); }
  to { transform: translate(40px, -40px) scale(1.1); }
}

/* ═══════════════════════════════════════
   TOOLBAR
   ═══════════════════════════════════════ */
.glass-toolbar {
  background: rgba(255, 255, 255, 0.6) !important;
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.6);
  position: relative;
  z-index: 2;
}

.toolbar-brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.back-link {
  text-decoration: none;
}

.btn-glass {
  background: rgba(0, 71, 155, 0.08) !important;
  color: #00479B !important;
  transition: all 0.3s ease;
}

.btn-glass:hover {
  background: rgba(0, 71, 155, 0.15) !important;
  transform: translateY(-1px);
}

.toolbar-title-text {
  font-size: 1.25rem;
  font-weight: 700;
  color: #0F172A;
  letter-spacing: -0.02em;
}

/* ═══════════════════════════════════════
   MAIN CONTENT
   ═══════════════════════════════════════ */
.main-container {
  position: relative;
  z-index: 1;
  padding-top: 1.5rem;
  padding-bottom: 3rem;
}

.signup-content {
  max-width: 520px;
  margin: 0 auto;
}

/* Welcome Block */
.welcome-block {
  text-align: center;
  margin-bottom: 2rem;
}

.welcome-eyebrow {
  display: inline-block;
  font-size: 0.875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #00479B;
  background: rgba(0, 71, 155, 0.06);
  padding: 0.35rem 0.9rem;
  border-radius: 999px;
  margin-bottom: 0.75rem;
}

.welcome-title {
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.03em;
  margin-bottom: 1rem;
  color: #0F172A;
}

.welcome-text {
  font-size: 1rem;
  color: #64748B;
  line-height: 1.7;
  max-width: 360px;
  margin: 0 auto;
}

/* Stepper */
.stepper-wrap {
  margin-bottom: 1.5rem;
  padding: 0 0.5rem;
}

.step-line {
  position: relative;
  height: 4px;
  background: #E2E8F0;
  border-radius: 2px;
  margin-bottom: 0.75rem;
  overflow: hidden;
}

.step-progress {
  height: 100%;
  background: linear-gradient(90deg, #00479B, #009688);
  border-radius: 2px;
  transition: width 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

.steps-row {
  display: flex;
  justify-content: space-between;
}

.step-dot {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  position: relative;
}

.step-dot > span,
.step-dot > .v-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #E2E8F0;
  color: #94A3B8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.step-dot.is-active > span,
.step-dot.is-active > .v-icon {
  background: linear-gradient(135deg, #00479B, #0066CC);
  color: white;
  box-shadow: 0 4px 12px rgba(0, 71, 155, 0.3);
  transform: scale(1.1);
}

.step-dot.is-completed > span,
.step-dot.is-completed > .v-icon {
  background: linear-gradient(135deg, #10B981, #059669);
  color: white;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.step-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: color 0.3s;
}

.step-dot.is-active .step-label,
.step-dot.is-completed .step-label {
  color: #0F172A;
}

/* Form Card */
.glass-form-card {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 1.25rem !important;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.02) !important;
}

/* Field Groups */
.field-group {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.field-icon-wrap {
  flex-shrink: 0;
}

.field-icon-circle {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: white;
  transition: all 0.3s ease;
}

.teal-glow {
  background: linear-gradient(135deg, #009688, #4DB6AC);
  box-shadow: 0 4px 12px rgba(0, 150, 136, 0.25);
  animation: subtlePulse 3s ease-in-out infinite;
}

.blue-glow {
  background: linear-gradient(135deg, #00479B, #0066CC);
  box-shadow: 0 4px 12px rgba(0, 71, 155, 0.25);
}

.orange-glow {
  background: linear-gradient(135deg, #F97316, #FB923C);
  box-shadow: 0 4px 12px rgba(249, 115, 22, 0.25);
}

@keyframes subtlePulse {
  0%, 100% { box-shadow: 0 4px 12px rgba(0, 150, 136, 0.25); }
  50% { box-shadow: 0 6px 20px rgba(0, 150, 136, 0.4); }
}

.field-input-wrap {
  flex: 1;
  min-width: 0;
}

/* Modern Inputs */
.modern-input >>> .v-input__slot {
  background: rgba(241, 245, 249, 0.6) !important;
  border-radius: 0.75rem !important;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.02) !important;
  border: 1px solid rgba(226, 232, 240, 0.8) !important;
  transition: all 0.3s ease;
  min-height: 48px;
  padding: 0 0.75rem !important;
}

.modern-input.v-input--is-focused >>> .v-input__slot {
  background: white !important;
  border-color: rgba(0, 150, 136, 0.4) !important;
  box-shadow: 0 0 0 3px rgba(0, 150, 136, 0.08), 0 4px 6px -1px rgba(0, 0, 0, 0.05) !important;
}

.modern-input >>> input {
  font-size: 1rem;
  font-weight: 500;
  color: #0F172A;
}

.modern-input >>> .v-label {
  color: #94A3B8;
  font-weight: 500;
}

/* Phone Input */
.phone-wrap {
  position: relative;
}

.phone-input {
  width: 100%;
  padding: 0.75rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(226, 232, 240, 0.8);
  background: rgba(241, 245, 249, 0.6);
  font-size: 1rem;
  font-weight: 500;
  color: #0F172A;
  outline: none;
  transition: all 0.3s ease;
  min-height: 48px;
}

.phone-input:focus {
  background: white;
  border-color: rgba(249, 115, 22, 0.4);
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.08), 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

/* intl-tel-input overrides */
.phone-wrap >>> .iti {
  width: 100%;
}

.phone-wrap >>> .iti__flag-container {
  padding: 0 0.5rem;
}

.phone-wrap >>> .iti__selected-flag {
  border-radius: 0.5rem 0 0 0.5rem;
}

/* Help Button */
.help-btn {
  width: 28px !important;
  height: 28px !important;
  border: 1.5px solid #CBD5E1 !important;
  color: #64748B !important;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.help-btn:hover {
  border-color: #00479B !important;
  color: #00479B !important;
  background: rgba(0, 71, 155, 0.04) !important;
}

.help-btn i {
  font-size: 0.7rem;
}

/* Submit */
.submit-wrap {
  margin-top: 1.5rem;
}

.btn-glass-primary {
  background: linear-gradient(135deg, #00479B, #0066CC) !important;
  color: white !important;
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: 0.01em;
  box-shadow: 0 4px 14px rgba(0, 71, 155, 0.3) !important;
  transition: all 0.3s ease;
  height: 48px !important;
}

.btn-glass-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 71, 155, 0.4) !important;
}

.btn-large {
  font-size: 1rem !important;
}

/* Login Link */
.login-link-wrap {
  text-align: center;
  margin-top: 1.5rem;
}

.login-text {
  color: #64748B;
  font-size: 0.95rem;
}

.login-link {
  color: #00479B;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.2s;
}

.login-link:hover {
  color: #0066CC;
  text-decoration: underline;
}

/* Trust Footer */
.signup-trust-footer {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 2rem;
  flex-wrap: wrap;
}

.trust-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  padding: 0.5rem 1rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #475569;
  box-shadow: 0 2px 4px rgba(0,0,0,0.04);
}

/* ═══════════════════════════════════════
   ANIMATIONS
   ═══════════════════════════════════════ */
.animate-enter {
  opacity: 0;
  transform: translateY(20px);
  animation: enterUp 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes enterUp {
  to { opacity: 1; transform: translateY(0); }
}

/* ═══════════════════════════════════════
   RESPONSIVE
   ═══════════════════════════════════════ */
@media (max-width: 600px) {
  .glass-toolbar { padding: 0 0.5rem; }
  .toolbar-title-text { font-size: 1.1rem; }
  .main-container { padding-top: 1rem; }
  .welcome-title { font-size: 1.75rem; }
  .glass-form-card { padding: 1.25rem !important; }
  .field-icon-circle { width: 38px; height: 38px; font-size: 1rem; }
  .field-group { gap: 0.5rem; }
  .step-label { font-size: 0.6rem; }
}

@media (min-width: 960px) {
  .main-container { padding-top: 2.5rem; }
  .glass-form-card { padding: 2rem !important; }
}
</style>